<script setup lang="ts">
import { RouterLink } from 'vue-router';
import { Button } from '@/components/ui/button';
import { useHead } from '@unhead/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import PaperCutout from '@app/presentation/components/paper/PaperCutout.vue';
import PaperFaqItem from '@app/presentation/components/landing/PaperFaqItem.vue';
import {
  APP_CTA_TO,
  APP_CTA_LABEL,
  TELEGRAM_CTA_LABEL,
  TELEGRAM_BOT_URL,
} from '@app/presentation/components/site/constants';

const faqItems = [
  {
    question: 'Что такое Таро Мяу?',
    answer: 'Таро Мяу (англ. Tarot Meow) — это сервис тёплых раскладов Таро. У нас 16 видов раскладов, чат с тарологом и обучение картам. Сервис доступен в Telegram и на сайте.',
  },
  {
    question: 'Какие расклады доступны бесплатно?',
    answer: 'Бесплатно доступны: Карта дня, Да или Нет, Задай вопрос (одна карта), Расклад на три карты. Все остальные расклады доступны по подписке.',
  },
  {
    question: 'Сколько стоит подписка?',
    answer: 'Подписка стоит 222\u20BD/неделя, 555\u20BD/месяц или 999\u20BD/год. С подпиской ты получаешь доступ ко всем 16 раскладам и чату без ограничений.',
  },
  {
    question: 'Как работает расклад?',
    answer: 'Ты выбираешь тип расклада, задаёшь вопрос, карты выпадают случайно. Кот анализирует комбинацию карт и даёт персональную интерпретацию.',
  },
  {
    question: 'Можно ли доверять интерпретации?',
    answer: 'Интерпретации опираются на тысячи значений и комбинаций карт Таро и носят рекомендательный характер.',
  },
  {
    question: 'Как начать пользоваться?',
    answer: 'Открой бота @TarotMeowBot в Telegram или перейди на сайт. Бесплатные расклады доступны сразу.',
  },
  {
    question: 'Что такое чат с тарологом?',
    answer: 'Это возможность задать дополнительные вопросы по твоему раскладу и получить развёрнутые ответы от таролога.',
  },
  {
    question: 'Сохраняются ли мои расклады?',
    answer: 'Да, вся история раскладов сохраняется в твоём профиле. Можно вернуться и перечитать в любое время.',
  },
  {
    question: 'Как отменить подписку?',
    answer: 'В профиле \u2192 Управление подпиской \u2192 Отменить. Подписка активна до конца оплаченного периода.',
  },
  {
    question: 'На каких языках доступен сервис?',
    answer: 'Русский и английский. Язык определяется автоматически или выбирается в настройках.',
  },
];

// Each answer is a sheet of stock laid a hair askew (a fixed pattern — static for the prerender).
const TILTS = [-0.3, 0.25, -0.15, 0.3, -0.25];

useHead({
  title: 'Частые Вопросы о Таро — FAQ | Таро Мяу',
  meta: [
    { name: 'description', content: 'Ответы на частые вопросы о Таро Мяу: как работает расклад Таро, какие расклады бесплатные, сколько стоит подписка, как начать пользоваться.' },
    { name: 'keywords', content: 'таро вопросы, гадание таро FAQ, как работает таро, таро онлайн вопросы' },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: 'Частые Вопросы о Таро — FAQ | Таро Мяу' },
    { property: 'og:description', content: 'Ответы на частые вопросы о Таро Мяу: как работает расклад, бесплатные расклады, подписка и многое другое.' },
    { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:site_name', content: 'Таро Мяу' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Частые Вопросы о Таро — FAQ | Таро Мяу' },
    { name: 'twitter:description', content: 'Ответы на частые вопросы о Таро Мяу: как работает расклад, бесплатные расклады, подписка и многое другое.' },
    { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
  ],
  link: [
    { rel: 'canonical', href: 'https://tarotmeow.ru/faq' },
    { rel: 'alternate', hreflang: 'ru', href: 'https://tarotmeow.ru/faq' },
    { rel: 'alternate', hreflang: 'x-default', href: 'https://tarotmeow.ru/faq' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
          { '@type': 'ListItem', position: 2, name: 'FAQ', item: 'https://tarotmeow.ru/faq' },
        ],
      }),
    },
  ],
});
</script>

<template>
  <div class="pb-8">
    <!-- Header -->
    <section class="py-16 text-center md:py-24">
      <ScrollReveal class="site-container">
        <div class="type-overline mb-3.5 text-primary-ink">
          Поддержка
        </div>
        <h1 class="type-display">
          Частые вопросы
        </h1>
        <p class="mx-auto mt-[18px] max-w-2xl text-[17px] text-muted-foreground md:text-lg">
          Ответы на популярные вопросы о Таро Мяу, раскладах и подписке
        </p>
      </ScrollReveal>
    </section>

    <!-- FAQ Items: sheets of card stock -->
    <section class="site-container">
      <div class="mx-auto max-w-[820px] space-y-3.5">
        <ScrollReveal
          v-for="(item, index) in faqItems"
          :key="index"
          :delay="80"
        >
          <PaperFaqItem
            :question="item.question"
            :answer="item.answer"
            heading="h2"
            :tilt="TILTS[index % TILTS.length]"
          />
        </ScrollReveal>
      </div>
    </section>

    <!-- Closing dual CTA: a note with the curious cat looking in from its corner (peek_cat is
         cropped right + bottom: both cut edges run past the note's edges). -->
    <section class="site-container py-16 md:py-24">
      <ScrollReveal>
        <div class="relative mx-auto max-w-3xl">
          <div class="paper-sheet-raised relative overflow-hidden px-8 pb-32 pt-9 text-center md:px-14 md:pb-14 md:pt-12">
            <div
              class="pointer-events-none absolute -bottom-2 -right-2 w-[128px] md:w-[160px]"
              aria-hidden="true"
            >
              <PaperCutout
                name="peek_cat"
                still
                lazy
              />
            </div>
            <h2 class="type-title">Остались вопросы?</h2>
            <p class="mx-auto mt-[18px] max-w-xl text-[17px] leading-relaxed text-muted-foreground sm:text-lg">
              Попробуй бесплатный расклад прямо сейчас — кот разложит карты под твой вопрос.
            </p>
            <div class="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button
                as-child
                size="lg"
              >
                <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
              </Button>
              <Button
                as-child
                variant="outline"
                size="lg"
              >
                <a
                  :href="TELEGRAM_BOT_URL"
                  target="_blank"
                  rel="noopener"
                >{{ TELEGRAM_CTA_LABEL }}</a>
              </Button>
            </div>
          </div>
        </div>
      </ScrollReveal>
    </section>
  </div>
</template>
